import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCertifications } from "@/hooks/useCertifications";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { toUserCert, type UserCertification } from "@/lib/certificationHelpers";
import { fetchActiveCertAccessOrNull, type CertAccessRow } from "@/lib/access";


/**
 * Returns the certifications a learner is allowed to access, derived from
 * `user_certification_access`. Rows that are revoked or past `expires_at`
 * are ignored. Admins/super_admins see every active cert.
 *
 * A failed refresh never clears a known good list: the previous rows are
 * kept and `isStale` is set, so guards do not mistake a network blip for
 * "this account lost access".
 */
export function useAccessibleCertifications() {
  const { user, isAdmin } = useAuth();
  const userId = user?.id ?? null;
  const adminCerts = useCertifications();
  const [rows, setRows] = useState<CertAccessRow[] | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const online = useOnlineStatus();

  const load = useCallback(
    async (isCancelled: () => boolean) => {
      if (!userId) {
        setRows([]);
        setIsStale(false);
        return;
      }
      const data = await fetchActiveCertAccessOrNull(userId);
      if (isCancelled()) return;
      if (data === null) {
        // Unknown, not empty. Keep whatever we already had, and fall back to
        // an empty list only on a cold start so the UI is not stuck loading.
        setIsStale(true);
        setRows((prev) => prev ?? []);
        return;
      }
      setIsStale(false);
      setHasLoadedOnce(true);
      setRows(data);
    },
    [userId],
  );

  useEffect(() => {
    setHasLoadedOnce(false);
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Coming back online retries a failed check so a bad first load recovers
  // without a manual reload. Only the offline -> online transition retries,
  // so a failure while online does not loop.
  const wasOnline = useRef(online);
  useEffect(() => {
    const cameBackOnline = online && !wasOnline.current;
    wasOnline.current = online;
    if (!cameBackOnline) return;
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [online, load]);



  const accessibleIds = (rows ?? []).map((r) => r.certification_id);
  const expiryById: Record<string, string | null> = {};
  for (const r of rows ?? []) expiryById[r.certification_id] = r.expires_at ?? null;

  const activeAdminCerts = adminCerts.filter((c) => c.status === "active");
  let visible: UserCertification[];
  if (isAdmin) {
    visible = activeAdminCerts.map(toUserCert);
  } else {
    const ids = new Set(accessibleIds);
    visible = activeAdminCerts.filter((c) => ids.has(c.certificationId)).map(toUserCert);
  }

  const hasCertAccess = (certId: string | null | undefined) =>
    !!certId && (isAdmin || accessibleIds.includes(certId));

  return {
    certifications: visible,
    accessibleIds,
    expiryById,
    hasCertAccess,
    hasAnyAccess: isAdmin || accessibleIds.length > 0,
    isLoaded: rows !== null || isAdmin,
    /** True when the last refresh failed and `accessibleIds` is last known good. */
    isStale,
    /** True once at least one access fetch succeeded this session. */
    hasLoadedOnce: hasLoadedOnce || isAdmin,

  };
}

